/**
 * @ngdoc directive
 * @name flowLibraryHeader
 * @module flowingly.runner.library
 * @description  This component is used to display the header for the flow library and will contain controls
 * @usage
 * ```
      <flow-library-header></flow-library-header>
 * ``` 
 */
import { SharedAngular } from '@Client/@types/sharedAngular';
import { EmptyGuid } from '@Shared.Angular/@types/guid';
import { FlowinglyPermissions } from '@Shared.Angular/flowingly.services/flowingly.constants';
import FlowModelType from '@Shared.Angular/@types/core/contracts/constants/flowModelType';
import angular from 'angular';
import { IStateService } from 'angular-ui-router';
import { STATE_MAPPINGS } from '@Shared.Angular/@types/stateMappings';
import type { DropDownButtonItemClickEvent } from '@progress/kendo-react-buttons';

angular.module('flowingly.runner.library').component('flowLibraryHeader', {
  bindings: {
    canCreateFlowModels: '<'
  },
  templateUrl: 'Client/runner.library/runner.library.header.tmpl.html',
  controller: [
    'tempModelerUrlService',
    '$state',
    'APP_CONFIG',
    'appInsightsService',
    'dialogService',
    'permissionsService',
    function (
      tempModelerUrlService: SharedAngular.TempModelerUrlService,
      $state: IStateService,
      APP_CONFIG: SharedAngular.APP_CONFIG,
      appInsightsService: SharedAngular.AppInsightsService,
      dialogService: SharedAngular.DialogService,
      permissionsService: SharedAngular.PermissionsService
    ) {
      this.currentTabName = window.location.pathname
        .split('/')
        .filter(Boolean)
        .pop();
      this.templateLibraryEnabled = APP_CONFIG.enableTemplateLibrary;

      this.hasProcedureCreatePermission =
        permissionsService.currentUserHasPermission(
          FlowinglyPermissions.PROCEDURE_CREATE
        );
      this.hasFormThemeCreatePermission =
        permissionsService.currentUserHasPermission(
          FlowinglyPermissions.FORM_THEME_CREATE
        );

      this.flowModelTypes = FlowModelType;
      this.allowProcessMap = APP_CONFIG.allowProcessMap;
      this.allowWorkflow = APP_CONFIG.allowWorkflow;

      const createProcessMapLabel = 'Process Map';
      const createProcessMapWithAiLabel = 'Process Map with AI';
      const createWorkflowLabel = 'Workflow';
      const createWorkflowWithAiLabel = 'Workflow with AI';
      this.newFlowModelDropDownItems = [
        this.allowProcessMap && createProcessMapLabel,
        this.allowProcessMap &&
          APP_CONFIG.enableFlowModelGeneration &&
          createProcessMapWithAiLabel,
        this.allowWorkflow && createWorkflowLabel,
        this.allowWorkflow &&
          APP_CONFIG.enableFlowModelGeneration &&
          createWorkflowWithAiLabel
      ].filter(Boolean);

      this.openModelerForNewFlowModel = (
        flowModelType: FlowModelType,
        generateWithAi: boolean
      ) => {
        tempModelerUrlService.openModeler({
          flowModelType,
          generateWithAi
        });
      };

      this.onFlowModelCreationMenuItemClick = (
        event: DropDownButtonItemClickEvent
      ) => {
        const selectedLabel = String(event.item);

        if (selectedLabel === createProcessMapLabel) {
          this.openModelerForNewFlowModel(FlowModelType.ProcessMap, false);
        } else if (selectedLabel === createProcessMapWithAiLabel) {
          this.openModelerForNewFlowModel(FlowModelType.ProcessMap, true);
        } else if (selectedLabel === createWorkflowLabel) {
          this.openModelerForNewFlowModel(FlowModelType.Workflow, false);
        } else if (selectedLabel === createWorkflowWithAiLabel) {
          this.openModelerForNewFlowModel(FlowModelType.Workflow, true);
        }
      };

      this.viewTemplatesClicked = () => {
        $state.go(STATE_MAPPINGS.templatesLibrary);
        appInsightsService.trackPageView('template-library', {
          from: 'library'
        });
      };

      this.importFlowModelsClicked = () => {
        dialogService.showDialog({
          template:
            'Client/runner.import/flowmodels/runner.import.flowmodels.dialog.tmpl.html',
          controller: 'importFlowModelsDialogController',
          appendClassName: 'ngdialog-normal'
        });
      };

      this.showImportFlowModelButton = () => {
        return (
          this.isViewingFlowModelTab() &&
          permissionsService.currentUserHasPermission(
            FlowinglyPermissions.FLOWMODEL_BULKIMPORT
          )
        );
      };

      this.showImportProcedureButton = () => {
        return (
          (this.currentTabName === 'sops' ||
            this.currentTabName === 'drafts') &&
          APP_CONFIG.enableProcedures &&
          permissionsService.currentUserHasPermission(
            FlowinglyPermissions.PROCEDURE_BULKIMPORT
          )
        );
      };

      this.showNewProcessMapButton = () =>
        !APP_CONFIG.enableCreationHub &&
        this.canCreateFlowModels &&
        this.isViewingFlowModelTab() &&
        this.allowProcessMap &&
        !APP_CONFIG.enableFlowModelGeneration &&
        !this.allowWorkflow;

      this.showNewWorkflowButton = () =>
        !APP_CONFIG.enableCreationHub &&
        this.canCreateFlowModels &&
        this.isViewingFlowModelTab() &&
        this.allowWorkflow &&
        !APP_CONFIG.enableFlowModelGeneration &&
        !this.allowProcessMap;

      this.showNewFlowModelButton = () =>
        !APP_CONFIG.enableCreationHub &&
        this.canCreateFlowModels &&
        this.isViewingFlowModelTab() &&
        ((this.allowProcessMap && this.allowWorkflow) ||
          (this.allowProcessMap && APP_CONFIG.enableFlowModelGeneration) ||
          (this.allowWorkflow && APP_CONFIG.enableFlowModelGeneration));

      this.showProcedureBulkImportModal = () => {
        this.showProcedureBulkImport = true;
      };

      this.closeProcedureBulkImportModal = () => {
        this.showProcedureBulkImport = false;
      };

      this.showCreateFormThemeButton = () => {
        return (
          this.currentTabName === 'formthemes' &&
          this.hasFormThemeCreatePermission
        );
      };

      this.onAddFormThemeClick = () => {
        $state.go(STATE_MAPPINGS.libraryFormTheme, {
          formThemeId: EmptyGuid
        });
      };

      this.isViewingFlowModelTab = () => {
        return (
          this.currentTabName !== 'sops' && this.currentTabName !== 'formthemes'
        );
      };
    }
  ]
});
